import { useState, useEffect } from 'react';
import { Plus, Trash, PencilSimple, Image, X, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

const EMPTY_AWARD = { title: '', caption: '', date: '', venue: '', image_url: '' };

export default function CMSAwardsPanel({ token }) {
  const [awards, setAwards] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_AWARD);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAwards = async () => {
    try {
      const res = await api.get('/cms/awards');
      setAwards(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAwards(); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/admin/cms/awards/${editing}?token=${token}`, form);
      } else {
        await api.post(`/admin/cms/awards?token=${token}`, form);
      }
      setEditing(null);
      setForm(EMPTY_AWARD);
      fetchAwards();
    } catch (e) { alert('Failed to save award'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this award?')) return;
    try {
      await api.delete(`/admin/cms/awards/${id}?token=${token}`);
      fetchAwards();
    } catch (e) { alert('Failed to delete'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/admin/upload?token=${token}`, formData);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      setForm(prev => ({ ...prev, image_url: `${API_URL}/api/files/${res.data.path}` }));
    } catch (e) { alert('Upload failed'); }
    finally { setUploading(false); }
  };

  const startEdit = (award) => {
    setEditing(award.id);
    setForm({ title: award.title, caption: award.caption, date: award.date, venue: award.venue, image_url: award.image_url });
  };

  if (loading) return <div className="text-center py-12 text-[#4A5568]">Loading awards...</div>;

  return (
    <div className="space-y-6" data-testid="cms-awards-panel">
      {/* Form */}
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-[#0A1F3D] mb-4">{editing ? 'Edit Award' : 'Add New Award'}</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Title *</label>
            <Input data-testid="award-title-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Award title" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Date</label>
            <Input data-testid="award-date-input" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} placeholder="e.g. 24th Feb 2024" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Venue</label>
            <Input data-testid="award-venue-input" value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} placeholder="Event venue" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Image</label>
            <div className="flex gap-2">
              <Input data-testid="award-image-input" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="Image URL or upload" className="rounded-none flex-1" />
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                <Button type="button" variant="outline" className="rounded-none" disabled={uploading}>
                  <Image size={16} /> {uploading ? '...' : 'Upload'}
                </Button>
              </label>
            </div>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-[#4A5568] mb-1">Caption</label>
          <Textarea data-testid="award-caption-input" value={form.caption} onChange={(e) => setForm({...form, caption: e.target.value})} placeholder="Award description/caption" rows={2} className="rounded-none" />
        </div>
        {form.image_url && (
          <div className="mb-4">
            <img src={form.image_url} alt="Preview" className="h-24 object-cover border" />
          </div>
        )}
        <div className="flex gap-2">
          <Button data-testid="award-save-btn" onClick={handleSave} disabled={!form.title} className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none">
            <FloppyDisk size={16} /> {editing ? 'Update' : 'Add Award'}
          </Button>
          {editing && (
            <Button variant="outline" onClick={() => { setEditing(null); setForm(EMPTY_AWARD); }} className="rounded-none">
              <X size={16} /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-bold text-[#0A1F3D]">{awards.length} Awards</h3>
        </div>
        {awards.length === 0 ? (
          <div className="text-center py-8 text-[#4A5568]">No awards yet. Add your first award above.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {awards.map((award) => (
              <div key={award.id} className="flex items-center gap-4 p-4 hover:bg-[#F5F5F5]/50" data-testid={`award-item-${award.id}`}>
                {award.image_url && (
                  <img src={award.image_url} alt="" className="w-16 h-12 object-cover flex-shrink-0 border" />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[#0A1F3D] text-sm truncate">{award.title}</h4>
                  <p className="text-xs text-[#4A5568] truncate">{award.caption}</p>
                  <div className="flex gap-3 mt-1">
                    <span className="text-xs text-[#D4AF37]">{award.date}</span>
                    {award.venue && <span className="text-xs text-[#4A5568]">{award.venue}</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(award)} className="p-2 hover:bg-slate-100 text-[#4A5568] hover:text-[#0A1F3D]" data-testid={`edit-award-${award.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => handleDelete(award.id)} className="p-2 hover:bg-red-50 text-[#4A5568] hover:text-red-500" data-testid={`delete-award-${award.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
